import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import translations from './praksisstedsok.i18n';
import * as css from './praksisstedsok.module.scss';
import { NumberInput, TextInput } from '../../components/input';
import Checkbox from '../../components/input/checkbox';
import { MinimalPraksisstedDto } from '../../api/generated/admin-generated-api';
import { ConfirmationDialog } from '../../components/dialog';
import { InformationDialog } from '../../components/dialog/informationDialog';
import { EmptyState } from '../../components/emptyState/emptyState';
import Button from '../../components/button';
import PanelHeader from '../../components/panelheader/panelHeader';
import { Search } from './types';

const SEARCH_RESULT_MIN_LIMIT = 1;
const SEARCH_RESULT_MAX_LIMIT = 100;
const MIN_SEARCH_LENGTH = 3;

type SearchFieldProps = {
  setSearch: (search: Search) => void;
  onSearch: () => void;
  search: Search;
  loading: boolean;
};

export const SearchField: React.FC<
  React.PropsWithChildren<SearchFieldProps>
> = ({ setSearch, onSearch, loading, search }) => {
  const { limit, searchString } = useMemo(() => search, [search]);
  const excludeDeletedChecked = useMemo(() => !search.includeDeleted, [search]);

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      searchLabel: formatMessage({ id: translations.searchLabel.id }),
      searchBtn: formatMessage({ id: translations.searchBtn.id }),
      searchPlaceholder: formatMessage({
        id: translations.searchPlaceholder.id,
      }),
      limitLabel: formatMessage({
        id: translations.limitLabel.id,
      }),
      limitPlaceholder: formatMessage(
        {
          id: translations.limitPlaceholder.id,
        },
        { min: SEARCH_RESULT_MIN_LIMIT, max: SEARCH_RESULT_MAX_LIMIT },
      ),
      gjeldendeOnlyCheckbox: formatMessage({
        id: translations.gjeldendeOnlyCheckbox.id,
      }),
      searchHeaderTitle: formatMessage({
        id: translations.searchHeaderTitle.id,
      }),
      searchHeaderSubtitle: formatMessage({
        id: translations.searchHeaderSubtitle.id,
      }),
    }),
    [formatMessage],
  );

  const invalidLimit = useMemo(
    () => !limit || limit > SEARCH_RESULT_MAX_LIMIT,
    [limit],
  );

  const invalidSearchString = useMemo(
    () => searchString.length < MIN_SEARCH_LENGTH,
    [searchString],
  );

  const searchIsDisabled = useMemo(
    () => loading || invalidLimit || invalidSearchString,
    [loading, invalidLimit, invalidSearchString],
  );

  const handleSearch = () => {
    if (searchIsDisabled) return;

    onSearch();
  };

  const handleKeyPress = (keyEvent: React.KeyboardEvent) => {
    if (keyEvent.key === 'Enter') handleSearch();
  };

  const setSearchStringHandler = (value: string) => {
    setSearch({ ...search, searchString: value });
  };

  const setLimitHandler = (lim: number | undefined) => {
    setSearch({ ...search, limit: lim });
  };

  const setExcludeDeletedHandler = (excludeDeleted: boolean) => {
    setSearch({ ...search, includeDeleted: !excludeDeleted });
  };

  return (
    <PanelHeader
      title={labels.searchHeaderTitle}
      subtitle={labels.searchHeaderSubtitle}
      collapsible
    >
      <div className={css.searchFieldWrapper}>
        <div className={css.searchAndLimit}>
          <TextInput
            containerClassName={css.searchField}
            htmlId={'praksissted-search'}
            label={labels.searchLabel}
            placeholder={labels.searchPlaceholder}
            onChange={setSearchStringHandler}
            onKeyPress={handleKeyPress}
            value={searchString}
          />
          <NumberInput
            containerClassName={css.limitField}
            htmlId={'praksissted-search-limit'}
            label={labels.limitLabel}
            placeholder={labels.limitPlaceholder}
            onChange={setLimitHandler}
            onKeyPress={handleKeyPress}
            value={limit}
            min={SEARCH_RESULT_MIN_LIMIT}
            max={SEARCH_RESULT_MAX_LIMIT}
          />
          <Button.Secondary
            className={css.searchBtn}
            disabled={searchIsDisabled}
            onClick={handleSearch}
            data-cy={'praksissted-search-btn'}
          >
            {labels.searchBtn}
          </Button.Secondary>
        </div>
        <Checkbox
          id={'praksissted-search-gjeldendeOnly'}
          wrapperClassname={css.gjeldendeOnlyCheckbox}
          checked={excludeDeletedChecked}
          setChecked={setExcludeDeletedHandler}
          label={labels.gjeldendeOnlyCheckbox}
        />
      </div>
    </PanelHeader>
  );
};

type ChoosePraksisstedMatchingSearchDialogProps = {
  praksisstederMatchingSearch: MinimalPraksisstedDto[];
  isOpen: boolean;
  onClose: (confirm: boolean, praksisstedId?: string) => void;
};

export const ChoosePraksisstedMatchingSearchDialog: React.FC<
  React.PropsWithChildren<ChoosePraksisstedMatchingSearchDialogProps>
> = ({ praksisstederMatchingSearch, isOpen, onClose }) => {
  const [chosenPraksisstedId, setChosenPraksisstedId] = useState<string>();

  useEffect(() => {
    if (!chosenPraksisstedId) return;

    if (
      !praksisstederMatchingSearch
        .map((praksissted) => praksissted.id)
        .includes(chosenPraksisstedId)
    ) {
      setChosenPraksisstedId(undefined);
    }
  }, [praksisstederMatchingSearch, chosenPraksisstedId]);

  const { formatMessage } = useIntl();
  const labels = {
    matchingPraksisstedDialogTitle: formatMessage({
      id: translations.matchingPraksisstedDialogTitle.id,
    }),
    matchingPraksisstedDialogSubtitle: formatMessage({
      id: translations.matchingPraksisstedDialogSubtitle.id,
    }),
    matchingPraksisstedDialogSubmit: formatMessage({
      id: translations.matchingPraksisstedDialogSubmit.id,
    }),
    matchingPraksisstedDialogNoResultTitle: formatMessage({
      id: translations.matchingPraksisstedDialogNoResultTitle.id,
    }),
    matchingPraksisstedDialogNoResultText: formatMessage({
      id: translations.matchingPraksisstedDialogNoResultText.id,
    }),
  };

  const onCloseHandler = (confirm: boolean) => {
    onClose(confirm, chosenPraksisstedId);
  };

  const handleKeyPress = (
    keyEvent: React.KeyboardEvent,
    praksisstedId: string,
  ) => {
    if (keyEvent.key === 'Enter') setChosenPraksisstedId(praksisstedId);
  };

  return praksisstederMatchingSearch.length > 0 ? (
    <ConfirmationDialog
      isOpen={isOpen}
      title={labels.matchingPraksisstedDialogTitle}
      subtitle={labels.matchingPraksisstedDialogSubtitle}
      submitLabel={labels.matchingPraksisstedDialogSubmit}
      submitDisabled={!chosenPraksisstedId}
      onClose={onCloseHandler}
    >
      <ul className={css.choosePraksisstedList}>
        {praksisstederMatchingSearch.map((sted) => {
          const { kode, navn, id } = sted;
          const isChosen = chosenPraksisstedId === id;

          return (
            <li
              key={`${kode}-${navn}`}
              className={isChosen ? css.currentPraksissted : undefined}
              onClick={() => setChosenPraksisstedId(id)}
              onKeyPress={(e) => handleKeyPress(e, id)}
              tabIndex={0}
              data-cy={`${kode}-${navn}`}
            >
              <p>
                {formatMessage(
                  {
                    id: translations.matchingPraksisstedDialogKode.id,
                  },
                  { kode },
                )}
              </p>
              <p>
                {formatMessage(
                  {
                    id: translations.matchingPraksisstedDialogNavn.id,
                  },
                  { navn },
                )}
              </p>
            </li>
          );
        })}
      </ul>
    </ConfirmationDialog>
  ) : (
    <InformationDialog
      isOpen={isOpen}
      title={labels.matchingPraksisstedDialogTitle}
      subtitle={labels.matchingPraksisstedDialogSubtitle}
      onClose={() => onCloseHandler(false)}
    >
      <EmptyState
        title={labels.matchingPraksisstedDialogNoResultTitle}
        text={labels.matchingPraksisstedDialogNoResultText}
      />
    </InformationDialog>
  );
};
